import React, {
  memo,
  useEffect,
  useRef,
  useMemo,
  useState,
  useCallback,
} from 'react';
import { useSelector } from 'react-redux';

import { FormHelperText } from '@material-ui/core';
import {
  KeyboardDateTimePicker,
  KeyboardDatePicker,
} from '@material-ui/pickers';
import { useField } from '@unform/core';
import { parseISO, format } from 'date-fns';
import { isEqual, isUndefined, isNull } from 'lodash';
import PropTypes from 'prop-types';

import { t, canURL } from '~/helpers';

import * as S from './styles';

function Pickers({ name, label, time, ...props }) {
  const [value, setValue] = useState(null);

  const { language } = useSelector((state) => state.user);
  const usesDayMonthYear = ['pt-BR', 'es-CL', 'es-ES'].includes(language);

  const inputRef = useRef(null);
  const noWrite = useMemo(() => isEqual(canURL(), { read: true }), []);

  const output = useMemo(() => {
    if (usesDayMonthYear) {
      if (time) return 'DD/MM/YYYY HH:mm:ss';
      return 'DD/MM/YYYY';
    }

    if (time) return 'YYYY-MM-DD HH:mm:ss';
    return 'YYYY-MM-DD';
  }, [usesDayMonthYear, time]);

  const outputValue = useMemo(() => {
    if (usesDayMonthYear) {
      if (time) return 'dd/MM/yyyy HH:mm:ss';
      return 'dd/MM/yyyy';
    }

    if (time) return 'yyyy-MM-dd HH:mm:ss';
    return 'yyyy-MM-dd';
  }, [usesDayMonthYear, time]);

  const {
    fieldName,
    defaultValue,
    registerField,
    error,
    clearError,
  } = useField(name);

  const handleDateChange = useCallback((data) => {
    if (isNull(data)) setValue(null);
    else if (data.isValid()) setValue(data.format('YYYY-MM-DD HH:mm:ss'));
  }, []);

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: inputRef.current,
      getValue: () => {
        if (!isNull(value)) {
          return format(parseISO(value), outputValue);
        }

        return value;
      },
      clearValue: () => setValue(null),
      setValue: (_, data) => setValue(data),
    });
  }, [fieldName, inputRef, defaultValue, value, time]);

  useEffect(() => {
    if (!isUndefined(defaultValue)) setValue(defaultValue);
  }, [defaultValue]);

  return (
    <S.Wrapper error={!!error}>
      {time ? (
        <KeyboardDateTimePicker
          ref={inputRef}
          ampm={!usesDayMonthYear}
          size="small"
          inputVariant="outlined"
          cancelLabel={t('s.geral.botaocancelar')}
          label={label}
          format={output}
          value={value}
          InputAdornmentProps={{ position: 'end' }}
          onChange={handleDateChange}
          onFocus={clearError}
          disabled={noWrite}
          error={error}
          {...props}
        />
      ) : (
        <KeyboardDatePicker
          ref={inputRef}
          size="small"
          inputVariant="outlined"
          cancelLabel={t('s.geral.botaocancelar')}
          label={label}
          format={output}
          value={value}
          InputAdornmentProps={{ position: 'end' }}
          onChange={handleDateChange}
          onFocus={clearError}
          disabled={noWrite}
          error={error}
          {...props}
        />
      )}

      <FormHelperText>{error}</FormHelperText>
    </S.Wrapper>
  );
}

Pickers.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  time: PropTypes.bool,
};

Pickers.defaultProps = {
  time: false,
};

export default memo(Pickers);
